import { ParcelBooleanFilterField, RANGE_MAX, type View } from "@/types"

type FilterExpression = unknown[]
type ParcelFilter = NonNullable<NonNullable<View["filter"]>["parcel"]>
type ParcelRangeFilterField = "superficy" | "dwellingUnits" | "storeys" | "propertyValue"

const PARCEL_RANGE_TILE_PROPERTIES: Record<ParcelRangeFilterField, string> = {
	superficy: "area_square_feet",
	dwellingUnits: "number_of_dwelling_units",
	storeys: "number_of_storeys",
	propertyValue: "total_property_value",
}

const PARCEL_BOOLEAN_TILE_PROPERTIES: Record<ParcelBooleanFilterField, string> = {
	[ParcelBooleanFilterField.PpuArea]: "policy_ppu_area",
	[ParcelBooleanFilterField.TodArea]: "policy_tod_area",
	[ParcelBooleanFilterField.SoilContaminated]: "env_soil_contaminated_bool",
	[ParcelBooleanFilterField.Wetland]: "env_wetland_bool",
	[ParcelBooleanFilterField.Rainette]: "env_rainette_bool",
}

const SENSITIVE_RANGE_FIELDS: ParcelRangeFilterField[] = ["dwellingUnits", "storeys", "propertyValue"]

function rangeConditions(tileProperty: string, range: ParcelFilter["superficy"]): FilterExpression[] {
	const min = range?.min ?? 0
	const max = range?.max ?? RANGE_MAX
	const value = ["to-number", ["get", tileProperty]]
	return [...(min > 0 ? [[">=", value, min]] : []), ...(max < RANGE_MAX ? [["<=", value, max]] : [])]
}

function buildingAgeConditions(range: ParcelFilter["buildingAge"]): FilterExpression[] {
	const minAge = range?.min ?? 0
	const maxAge = range?.max ?? RANGE_MAX
	if (minAge <= 0 && maxAge >= RANGE_MAX) return []

	const currentYear = new Date().getFullYear()
	const constructionYear = ["to-number", ["get", "construction_year"]]
	return [
		[">", constructionYear, 0],
		...(minAge > 0 ? [["<=", constructionYear, currentYear - minAge]] : []),
		...(maxAge < RANGE_MAX ? [[">=", constructionYear, currentYear - maxAge]] : []),
	]
}

export function cadastreFilterForView(view: View, hasSensitiveAccess: boolean): FilterExpression | null {
	const filter = view.filter?.parcel

	const rangeFields: ParcelRangeFilterField[] = hasSensitiveAccess
		? ["superficy", ...SENSITIVE_RANGE_FIELDS]
		: ["superficy"]
	const conditions = rangeFields.flatMap(field => rangeConditions(PARCEL_RANGE_TILE_PROPERTIES[field], filter?.[field]))

	if (hasSensitiveAccess) {
		conditions.push(...buildingAgeConditions(filter?.buildingAge))
		for (const field of Object.values(ParcelBooleanFilterField)) {
			const value = filter?.[field]
			if (value == null) continue
			conditions.push(["==", ["get", PARCEL_BOOLEAN_TILE_PROPERTIES[field]], value])
		}
	}

	if (hasSensitiveAccess && !filter?.showStreets) {
		conditions.push(["!=", ["get", "is_street"], true])
	}

	return conditions.length > 0 ? ["all", ...conditions] : null
}
